import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Globe2, Loader2, PlugZap, SearchCheck, ShoppingCart, TriangleAlert } from 'lucide-react';
import { Banner, Button, Card, EmptyState, Fact, Facts, PageHeader, StatusPill, TabPanel, TableSkeleton, Tabs } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { PurchaseWizard } from '@/components/domains/PurchaseWizard';
import { DomainCheckCard } from '@/components/domains/DomainCheckCard';
import { useDomainPurchases } from '@/hooks/useDomainPurchases';
import { formatRelative } from '@/utils/format';
import { PURCHASE_STATUS } from '@/utils/labels';

type DomainsTab = 'purchases' | 'check';

/**
 * Buying domains through 2d2s (ADR-0039), for the owner and administrators,
 * in two tabs: the purchases made from this panel — 2d2s holds its whole
 * team's — each under way followed live, a row opening the wizard at its
 * step (ADR-0053); and the check of names before they are bought (ADR-0050).
 */
export function DomainsPage(): ReactNode {
  const overview = useDomainPurchases();
  const [wizard, setWizard] = useState<{ purchaseId: string | null } | null>(null);
  const [params, setParams] = useSearchParams();
  const tab: DomainsTab = params.get('tab') === 'check' ? 'check' : 'purchases';
  const setTab = (next: DomainsTab) => setParams(next === 'purchases' ? {} : { tab: next }, { replace: true });
  const data = overview.data;
  const active = data?.purchases.filter((purchase) => !purchase.finished).length ?? 0;

  return (
    <div className="page">
      <PageHeader
        title="Домены"
        subtitle="Проверка и покупка доменов через 2d2s"
        actions={
          <Button variant="primary" icon={ShoppingCart} disabled={!data?.available} onClick={() => setWizard({ purchaseId: null })}>
            Купить домены
          </Button>
        }
      />

      <Tabs
        label="Домены"
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'purchases', label: 'Покупки', icon: ShoppingCart, ...(active > 0 ? { count: active } : {}) },
          { id: 'check', label: 'Проверка доменов', icon: SearchCheck },
        ]}
      />

      <TabPanel id="check" active={tab}>
        <DomainCheckCard />
      </TabPanel>

      <TabPanel id="purchases" active={tab}>
        {overview.isPending ? (
          <Card flush>
            <TableSkeleton rows={3} columns={5} />
          </Card>
        ) : overview.isError ? (
          <ErrorState error={overview.error} />
        ) : data ? (
          <div className="stack">
            {!data.available ? (
              <Banner tone="warning" icon={PlugZap}>
                2d2s не подключён: не задан ключ API (TWOD2S_API_KEY).
              </Banner>
            ) : (
              <Card>
                <Facts>
                  <Fact label="Баланс для покупок">
                    {data.budget
                      ? new Intl.NumberFormat('ru-RU', { style: 'currency', currency: data.budget.currency }).format(data.budget.balance)
                      : 'не удалось узнать у 2d2s'}
                  </Fact>
                  <Fact label="Теги купленных доменов">{data.tags.length > 0 ? data.tags.join(', ') : 'не заданы'}</Fact>
                </Facts>
              </Card>
            )}

            {data.available && data.budget && data.budget.balance <= 0 ? (
              <Banner tone="warning" icon={TriangleAlert}>
                Баланс 2d2s пуст. Проверка доступности работает, покупка — после пополнения.
              </Banner>
            ) : null}

            <Card flush title="Покупки">
              {data.purchases.length === 0 ? (
                <EmptyState icon={Globe2} message="Покупок пока нет" />
              ) : (
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Когда</th>
                        <th scope="col">Кто</th>
                        <th scope="col">Имён</th>
                        <th scope="col">Куплено</th>
                        <th scope="col">Состояние</th>
                        <th scope="col" className="shrink" />
                      </tr>
                    </thead>
                    <tbody>
                      {data.purchases.map((purchase) => (
                        <tr key={purchase.id}>
                          <td>{formatRelative(purchase.createdAt)}</td>
                          <td>{purchase.requestedBy}</td>
                          <td>{purchase.candidates}</td>
                          <td className="mono">{purchase.purchased.length > 0 ? purchase.purchased.join(', ') : '—'}</td>
                          <td>
                            <span className="row-tight">
                              {!purchase.finished ? <Loader2 size={14} className="spin muted" aria-hidden="true" /> : null}
                              <StatusPill map={PURCHASE_STATUS} status={purchase.status} />
                            </span>
                            {!purchase.finished ? <div className="muted text-sm">изменилось {formatRelative(purchase.updatedAt)}</div> : null}
                            {purchase.siteOrderId ? (
                              <Link className="text-sm" to={`/sites?order=${purchase.siteOrderId}`}>
                                Для нового сайта
                              </Link>
                            ) : null}
                          </td>
                          <td className="shrink">
                            <Button size="sm" variant={purchase.finished ? 'default' : 'primary'} disabled={!data.available} onClick={() => setWizard({ purchaseId: purchase.id })}>
                              {purchase.finished ? 'Открыть' : 'Следить'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        ) : null}
      </TabPanel>

      {data && wizard ? <PurchaseWizard open purchaseId={wizard.purchaseId} overview={data} onClose={() => setWizard(null)} /> : null}
    </div>
  );
}
